import { Box, Modal } from "@mui/material";
import ConfigView from "components/organisms/ConfigView";
import VirtualKeyboard from "components/organisms/KeyboardView";
import ToggleKioskPasswordPrompt from "components/organisms/ToggleKioskPasswordPrompt";
import {
  CONFIG_REQUIRED,
  SHOW_INACTIVITY_MODAL,
  SHOW_ON_SCREEN_KEYBOARD,
} from "config/envConfig";
import Header from "container/header/header";
import ProductDetailsModal from "container/productDetailsModal/productDetailsModal";
import { CartContext } from "context/cartContext";
import { AccessbilityViewHeight, useNav } from "context/navContext";
import InactivityHandler from "InActivityHandler";
import { useContext, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  ALLOW_ALL_STORES_KEY,
  DATA_REFERENCE,
  ENABLE_SCAN_KEY,
  SCAN_MODE_KEY,
  STORE_LIST_KEY,
} from "utils/Constants";
import isJFK, { isTab } from "utils/helperFunctions";
import { XIconBlack } from "utils/iconList";
import RouteSwitch from "../../routes/routeSwitch";
import { useOnlineStatus } from "offline/OfflineScreen";
import { getProductDetails } from "utils/apiUtil";

function PageWrapper() {
  const { showHeader, accessibilityMode, isKeyboardOpen } = useNav();
  const isOnline = useOnlineStatus();
  const useCart = useContext(CartContext);

  const location = useLocation();
  const isAnyPaymentPage = location.pathname?.includes("payment");

  const isPaymentProcessPage = location.pathname === "/paymentProcess";

  const config = localStorage.getItem(DATA_REFERENCE);

  const [showPasswordPrompt, setShowPasswordPrompt] = useState(false);
  const [tapCount, setTapCount] = useState(0);
  const [tapTimeoutId, setTapTimeoutId] = useState(null);
  const [showConfigView, setShowConfigView] = useState(false);

  const [keyboardLayout, setKeyboardLayout] = useState("standard");
  const [keyboardValue, setKeyboardValue] = useState("");
  const [activeInput, setActiveInput] = useState(null);

  // Scan QR logic (active when enableScan is true, for both scanOnly and scanAndBrowse modes)
  const parsedConfig = (() => {
    try {
      return JSON.parse(config) || {};
    } catch {
      return {};
    }
  })();
  const isScanEnabled = parsedConfig[ENABLE_SCAN_KEY] ?? false;

  const [scanLoading, setScanLoading] = useState(false);
  const [scanError, setScanError] = useState(null);
  const [scanProductData, setScanProductData] = useState(null);
  const [openScanModal, setOpenScanModal] = useState(false);
  const [scanDefaults, setScanDefaults] = useState({});

  const isProcessingScanRef = useRef(false);
  const lastScannedRef = useRef(null);
  const lastScanTimeRef = useRef(0);
  const handleAddFromQRRef = useRef(null);

  const normalizeProductId = (val) => {
    const cleaned = val
      .replace(/[\r\n\t\s]/g, "")
      .trim()
      .replace(/-/g, "_");
    return cleaned.slice(0, 2).toLowerCase() === "ov"
      ? `OV${cleaned.slice(2)}`
      : cleaned;
  };

  const preprocessScanResponse = (res) => {
    if (res.masterVariants?.productVariants) {
      res.masterVariants.productVariants =
        res.masterVariants.productVariants.filter((f) => f.isChoice === true);
    }
    if (res.pDetails?.attributeList) {
      res.pDetails.attributeList = res.pDetails.attributeList.filter(
        (f) => f.isChoice === true
      );
    }
    if (res.pDetails?.addons) {
      res.pDetails.addons.forEach((el) => {
        el.selected = false;
      });
    }
    return res;
  };

  const addToCartFromScan = (pDetails) => {
    const existingItem = useCart
      .getItems()
      .find((item) => item.itemId === pDetails._id);

    if (existingItem) {
      useCart.updateItem({
        storecode: pDetails.storeInfo?._id,
        productId: pDetails._id,
        quantity: existingItem.itemQuantity + 1,
      });
    } else {
      const payload = useCart.mapProductToCartPayload({ pDetails });
      useCart.addItem({
        storecode: payload.storeId,
        storeName: payload.items[0].storeName,
        movementType: payload.movementType,
        sector: payload.sector,
        terminal: payload.terminal,
        domain: payload.domain,
        fulfilmentType: payload.fulfilmentType,
        item: payload.items[0],
      });
    }
  };

  const handleAddFromQR = async (rawProductId) => {
    const productId = normalizeProductId(rawProductId);
    const now = Date.now();

    if (
      lastScannedRef.current === productId &&
      now - lastScanTimeRef.current < 1000
    )
      return;
    lastScannedRef.current = productId;
    lastScanTimeRef.current = now;

    if (isProcessingScanRef.current) return;
    isProcessingScanRef.current = true;
    setScanLoading(true);
    setScanError(null);

    try {
      const res = await getProductDetails(productId);

      if (!res?.pDetails) {
        setScanError({
          title: "Item Not Found",
          message: `No item matched the scanned barcode "${productId}". Please try scanning again.`,
        });
        return;
      }

      // Single-store validation (applies to both scanOnly and scanAndBrowse)
      const allowAll =
        parsedConfig[ALLOW_ALL_STORES_KEY] !== undefined
          ? parsedConfig[ALLOW_ALL_STORES_KEY]
          : true;
      const storeList = Array.isArray(parsedConfig[STORE_LIST_KEY])
        ? parsedConfig[STORE_LIST_KEY]
        : [];
      if (!allowAll && storeList.length === 1) {
        const productStoreId = res.pDetails?.storeInfo?._id;
        if (productStoreId && productStoreId !== storeList[0]) {
          setScanError({
            title: "Wrong Store",
            message:
              "This item is not available at the selected store. Please scan an item from the correct store.",
          });
          return;
        }
      }

      preprocessScanResponse(res);

      const hasPreferences =
        res.pDetails?.preferences?.length > 0 ||
        res.pDetails?.attributeList?.length > 0;

      if (hasPreferences) {
        setScanDefaults({});
        setScanProductData(res);
        setOpenScanModal(true);
      } else {
        addToCartFromScan(res.pDetails);
      }
    } catch (e) {
      console.error("Scan add failed", e);
      setScanError({
        title: "Scan Error",
        message: "Something went wrong. Please try scanning again.",
      });
    } finally {
      setScanLoading(false);
      isProcessingScanRef.current = false;
    }
  };

  handleAddFromQRRef.current = handleAddFromQR;

  const handleScanRefreshItem = async (productId) => {
    try {
      const res = await getProductDetails(productId);
      if (res?.pDetails) {
        preprocessScanResponse(res);
        setScanProductData(res);
      }
    } catch (e) {
      console.error("Scan variant refresh failed", e);
    }
  };

  useEffect(() => {
    if (!isScanEnabled) return;

    let qrValue = "";
    let scanTimer = null;

    function flushScan() {
      const val = normalizeProductId(qrValue);
      qrValue = "";
      scanTimer = null;
      if (val.length >= 5) {
        handleAddFromQRRef.current?.(val);
      }
    }

    function handleScanner(event) {
      if (
        event.target.tagName === "INPUT" ||
        event.target.tagName === "TEXTAREA" ||
        event.target.isContentEditable
      ) {
        return;
      }
      if (["Enter", "Tab"].includes(event.key) || event.key === "\r") {
        clearTimeout(scanTimer);
        flushScan();
        return;
      }
      if (
        event.key.length === 1 &&
        !event.ctrlKey &&
        !event.metaKey &&
        event.key !== "\n" &&
        event.key !== "\r"
      ) {
        qrValue += event.key;
        clearTimeout(scanTimer);
        scanTimer = setTimeout(flushScan, 300);
      }
    }

    window.addEventListener("keydown", handleScanner, true);
    return () => {
      window.removeEventListener("keydown", handleScanner, true);
      clearTimeout(scanTimer);
    };
  }, [isScanEnabled]);

  const handleToggleTap = () => {
    if (tapTimeoutId) clearTimeout(tapTimeoutId);
    const newTapCount = tapCount + 1;
    setTapCount(newTapCount);

    if (newTapCount === 5) {
      setShowPasswordPrompt(true);
      setTapCount(0);
    } else {
      setTapTimeoutId(setTimeout(() => setTapCount(0), 2000));
    }
  };

  useEffect(() => {
    const handleFocus = (e) => {
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA") {
        const type = e.target.type;
        setKeyboardValue(e.target.value || "");
        if (["text", "number", "tel", "email", "password"].includes(type)) {
          setActiveInput(e.target);
        }
        setKeyboardLayout(
          type === "number" || type === "tel" ? "numeric" : "standard"
        );
      }
    };

    function handleClickOutside(event) {
      const keyboardEl = document.getElementById("keyboard-container");

      if (
        keyboardEl?.contains(event.target) ||
        event.target.tagName === "INPUT" ||
        event.target.tagName === "TEXTAREA"
      )
        return;
      setActiveInput(null);
    }

    document.addEventListener("focusin", handleFocus);
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("focusin", handleFocus);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const containerRef = useRef(null);

  const scrollToBottom = () => {
    containerRef.current?.scrollTo({
      top: containerRef.current.scrollHeight,
      behavior: "smooth",
    });
  };

  const isStartRoute = location.pathname.includes("start");
  
  // DEBUGGING LINES FOR ACCESSIBILITY VIEW HEIGHT
  console.log("----- ACCESSIBILITY VIEW DEBUG -----");
  console.log("1. Location Pathname: ", location.pathname);
  console.log("2. isStartRoute (pathname includes 'start'): ", isStartRoute);
  console.log("3. isKeyboardOpen: ", isKeyboardOpen);
  console.log("4. accessibilityMode: ", accessibilityMode);
  console.log("5. Default AccessbilityViewHeight: ", AccessbilityViewHeight);

  useEffect(() => {
    if (accessibilityMode && isStartRoute) {
      requestAnimationFrame(() => scrollToBottom());
    }
  }, [accessibilityMode, isStartRoute]);

  const _accessibilityViewHeight = isKeyboardOpen
    ? "60vh"
    : isStartRoute
      ? "90vh"
      : AccessbilityViewHeight;

  console.log("6. FINAL _accessibilityViewHeight applied: ", _accessibilityViewHeight);
  console.log("-------------------------------------");

  return (
    <>
      {(showConfigView || (config === null && CONFIG_REQUIRED)) && (
        <ConfigView />
      )}
      {showHeader && (
        <Header
          style={{
            top: !accessibilityMode
              ? "0px"
              : `calc(100vh - ${_accessibilityViewHeight})`,
          }}
          hideLogoAndGoBack={isAnyPaymentPage}
          hideStartOver={isAnyPaymentPage}
        />
      )}
      <main
        ref={containerRef}
        style={{
          paddingTop: showHeader ? (isTab() ? "98px" : "118px") : "0px",
          height: !accessibilityMode
            ? showHeader
              ? isTab()
                ? "calc(100vh - 98px)"
                : "calc(100vh - 118px)"
              : "100vh"
            : `calc(${_accessibilityViewHeight} - ${isTab() ? "98px" : "118px"})`,
          position: accessibilityMode ? "absolute" : "relative",
          bottom: accessibilityMode ? "0px" : "auto",
          display: "flex",
        }}
      >
        {SHOW_INACTIVITY_MODAL === "true" && <InactivityHandler />}
        <RouteSwitch />
      </main>
      {(location.pathname.includes("start") ||
        location.pathname === "/" ||
        !isOnline) && (
        <div
          className="absolute top-0 right-0 w-[150px] h-[150px] bg-transparent z-10"
          onClick={handleToggleTap}
        >
          {tapCount > 0 && (
            <div className="absolute top-4 right-4 bg-gray-500 w-[30px] h-[30px] flex items-center justify-center rounded-full opacity-50">
              <span className="text-white font-bold text-xl">{tapCount}</span>
            </div>
          )}
        </div>
      )}
      {showPasswordPrompt && (
        <ToggleKioskPasswordPrompt
          onClose={() => setShowPasswordPrompt(false)}
          onEditConfig={() => {
            setShowPasswordPrompt(false);
            setShowConfigView(true);
          }}
        />
      )}
      {SHOW_ON_SCREEN_KEYBOARD && <VirtualKeyboard />}
      {isScanEnabled && scanLoading && (
        <div
          style={{
            position: "fixed",
            ...(accessibilityMode
              ? {
                  bottom: 0,
                  left: 0,
                  right: 0,
                  top: `calc(100vh - ${_accessibilityViewHeight})`,
                  maxHeight: _accessibilityViewHeight,
                }
              : { inset: 0 }),
            background: "rgba(0,0,0,0.45)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 999999,
            gap: "24px",
          }}
        >
          <div
            style={{
              width: "64px",
              height: "64px",
              border: "6px solid rgba(255,255,255,0.3)",
              borderTop: "6px solid #fff",
              borderRadius: "50%",
              animation: "spin 0.8s linear infinite",
            }}
          />
          <span
            style={{
              color: "#fff",
              fontSize: "22px",
              fontWeight: "500",
              letterSpacing: "0.02em",
            }}
          >
            Adding item...
          </span>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      )}
      {isScanEnabled && !!scanError && (
        <Modal
          open={!!scanError}
          onClose={() => setScanError(null)}
          sx={
            accessibilityMode
              ? {
                  top: `calc(100vh - ${_accessibilityViewHeight})`,
                  maxHeight: _accessibilityViewHeight,
                }
              : {}
          }
        >
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: isTab() ? "calc(100% - 60px)" : "954px",
              bgcolor: "background.paper",
              border: "none",
              boxShadow: 24,
              borderRadius: 3,
            }}
          >
            <div
              style={{
                position: "absolute",
                top: "32px",
                right: "32px",
                cursor: "pointer",
                zIndex: 9,
              }}
              onClick={() => setScanError(null)}
            >
              <img
                src={XIconBlack}
                alt="close"
                style={{ width: 32, height: 32 }}
              />
            </div>
            <div style={{ padding: "32px" }}>
              <div
                style={{
                  fontWeight: 700,
                  fontSize: "32px",
                  color: "var(--text-black)",
                  lineHeight: "130%",
                }}
              >
                {scanError.title}
              </div>
              <div
                style={{
                  fontSize: "28px",
                  color: "var(--text-black)",
                  lineHeight: "130%",
                  paddingTop: "32px",
                  opacity: 0.7,
                }}
              >
                {scanError.message}
              </div>
              <div
                style={{
                  marginTop: "48px",
                  display: "flex",
                  justifyContent: "flex-end",
                }}
              >
                <button
                  onClick={() => setScanError(null)}
                  style={{
                    padding: "12px 48px",
                    fontSize: "30px",
                    lineHeight: "130%",
                    cursor: "pointer",
                    backgroundColor: "var(--primary)",
                    border: "2px solid var(--primary)",
                    color: "var(--primary-foreground)",
                    borderRadius: isJFK() ? "0px" : "12px",
                  }}
                >
                  Dismiss
                </button>
              </div>
            </div>
          </Box>
        </Modal>
      )}
      {isScanEnabled && openScanModal && scanProductData && (
        <ProductDetailsModal
          open={openScanModal}
          data={scanProductData}
          setOpenDetailsModal={setOpenScanModal}
          setSelectedProductsDetails={setScanProductData}
          defaults={scanDefaults}
          setDefaults={setScanDefaults}
          selectedPreferences={[]}
          selectedPreferencesDetails={[]}
          refreshItemData={handleScanRefreshItem}
          handleSubmit={() => setOpenScanModal(false)}
          isEdit={false}
          shopDetails={null}
        />
      )}
    </>
  );
}

export default PageWrapper;
